import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { PageHeader } from "../components/app/PageHeader";

const empty = { name: "", code: "", zone: "", patron: "", local_area: "", address: "", estimated_households: "", units: [] };
const META = [["code", "GKK ID (e.g. GKK-057)"], ["zone", "Zone (e.g. Zone 1)"], ["patron", "Patron / community name"], ["local_area", "Local area"], ["address", "Address (purok level only)"]];

const MasterImport = ({ onDone }) => {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [rep, setRep] = useState(null);
  const send = async (commit) => {
    const fd = new FormData(); fd.append("file", file); fd.append("commit", commit ? "true" : "false");
    try { const r = await api.post("/gkks/import", fd); setRep(r.data); if (commit) { toast.success("GKK list updated"); onDone(); } } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-8 rounded-xl border border-amber-700/15 bg-[#F5F0E6] p-5" data-testid="master-import-card">
      <p className="flex items-center gap-2 font-semibold text-stone-800"><FileSpreadsheet className="h-4 w-4" /> Update from Excel master list</p>
      <p className="mt-1 text-xs text-stone-600">Columns: GKK ID, Zone, GKK / Community Name, Local Area for Dropdown, Suggested Dropdown Label, Address (Source Roster). Matched by GKK ID — existing GKKs are updated, never duplicated.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setRep(null); }} data-testid="master-file-input" />
        <Button variant="outline" onClick={() => ref.current.click()} data-testid="master-choose-button">{file ? file.name : "Choose .xlsx"}</Button>
        <Button variant="outline" disabled={!file} onClick={() => send(false)} data-testid="master-check-button">Check</Button>
        <Button disabled={!rep || rep.status !== "validated"} onClick={() => send(true)} className="bg-[#285E3B] hover:bg-[#1E462C]" data-testid="master-save-button">Save</Button>
      </div>
      {rep && <p className="mt-3 text-sm text-stone-700" data-testid="master-import-report">{rep.status === "committed" ? "Saved" : "Check"}: {rep.total_rows} rows · {rep.created} new · {rep.updated} updated · {rep.unchanged} unchanged · {rep.rejected} rejected{rep.errors.map((e) => ` · row ${e.row}: ${e.errors.join("; ")}`).join("")}</p>}
    </div>
  );
};

const GkkForm = ({ form, setForm }) => {
  const setUnit = (i, name) => setForm({ ...form, units: form.units.map((u, j) => (j === i ? { ...u, name } : u)) });
  return (
    <div className="space-y-3">
      <Input placeholder="Display label (unique), e.g. Santo Niño – Arizala-Paradise (Zone 1)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="gkk-name-input" />
      {META.map(([k, l]) => <Input key={k} placeholder={l} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`gkk-${k}-input`} />)}
      <Input type="number" placeholder="Estimated number of households (optional)" value={form.estimated_households ?? ""} onChange={(e) => setForm({ ...form, estimated_households: e.target.value })} data-testid="gkk-estimate-input" />
      <p className="text-xs font-semibold text-stone-600">Units</p>
      {form.units.map((u, i) => (
        <div key={i} className="flex gap-2">
          <Input value={u.name} onChange={(e) => setUnit(i, e.target.value)} placeholder="Unit name" data-testid={`unit-name-input-${i}`} />
          <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, units: form.units.filter((_, j) => j !== i) })} data-testid={`unit-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setForm({ ...form, units: [...form.units, { name: "" }] })} data-testid="add-unit-button"><Plus className="h-4 w-4 mr-1" />Add unit</Button>
    </div>
  );
};

export default function MasterList() {
  const [gkks, setGkks] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/gkks").then((r) => setGkks(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { name: form.name, ...Object.fromEntries(META.map(([k]) => [k, form[k] || null])), estimated_households: form.estimated_households === "" || form.estimated_households === null ? null : Number(form.estimated_households), units: form.units.filter((u) => u.name.trim()) };
    try {
      if (form.gkk_id) await api.patch(`/gkks/${form.gkk_id}`, body); else await api.post("/gkks", body);
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (g) => {
    try { await api.delete(`/gkks/${g.gkk_id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Configuration" title="GKK & Unit List" testId="master-header">
        The official list of GKKs and Units used to validate imports. Enter names exactly as the parish uses them. Communities with survey records cannot be deleted.
      </PageHeader>
      <MasterImport onDone={load} />
      <Button onClick={() => setForm({ ...empty })} className="mb-6 bg-amber-900 hover:bg-[#5B210B]" data-testid="add-gkk-button"><Plus className="h-4 w-4 mr-2" />Add GKK</Button>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {gkks.map((g) => (
          <div key={g.gkk_id} className="rounded-xl border border-amber-700/15 bg-white p-5" data-testid={`gkk-card-${g.gkk_id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-800/80">{g.code || "No ID"} · {g.zone || "No zone"}</p>
                <p className="font-serif-display text-xl font-semibold text-amber-950">{g.patron || g.name}</p>
                <p className="text-xs text-stone-600">{g.local_area}</p>
                {g.is_test && <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-700">Test record</span>}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => setForm({ ...g })} data-testid={`edit-gkk-${g.gkk_id}`}><Pencil className="h-4 w-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="ghost" size="icon" data-testid={`delete-gkk-${g.gkk_id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent className="bg-white">
                    <AlertDialogHeader><AlertDialogTitle>Delete {g.name}?</AlertDialogTitle><AlertDialogDescription>Only possible when no survey records belong to this GKK.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(g)} data-testid={`confirm-delete-gkk-${g.gkk_id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <p className="mt-2 text-xs text-stone-500">{g.address || "No address"} · Estimated households: {g.estimated_households ?? "not set"}</p>
            <p className="mt-2 text-sm text-stone-700">{g.units.length ? `Units: ${g.units.map((u) => u.name).join(" · ")}` : "No units listed"}</p>
          </div>
        ))}
        {!gkks.length && <p className="text-sm text-stone-500" data-testid="no-gkks">No GKKs yet. Add your parish's GKKs to begin importing survey data.</p>}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{form?.gkk_id ? "Edit GKK" : "Add GKK"}</DialogTitle></DialogHeader>
          {form && <GkkForm form={form} setForm={setForm} />}
          <DialogFooter><Button onClick={save} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="save-gkk-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
